
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js';
import UtilsService from '@/services/utils.service';
import _ from 'lodash';

@Component({
  components: {
  },
})
export default class StackedBarChart extends Vue {
  public showingTruncatedWarning: boolean = false;

  public currentlyShownAttribute: any = null;
  public currentlyShownLabels: any = null;

  @Prop({ default: 0 })
  public resultIndex: number;
  
  get result() {
    return this.$store.state.results.result;
  }

  get graphIndex() {
    return this.$store.state.results.selectedGraphIndex || 0;
  }

  get clearGraphStickyColumnsTrigger() {
    return this.$store.state.profiles.clearGraphStickyColumnsTrigger;
  }

  public get optionsData() {
    return this.$store.state.options.options || {};
  }


  public chart: any;

  @Watch('graphIndex')
  public graphIndexChanged(value: string, oldValue: string) {
    this.currentlyShownAttribute = null; // make no further attempt to keep same attribute selected
    this.createGraph();
  }

  @Watch('clearGraphStickyColumnsTrigger')
  private onClearGraphStickyColumnsTriggerChange(value: string, oldValue: string) {
    this.currentlyShownAttribute = null; // make no further attempt to keep same attribute selected
    this.currentlyShownLabels = null; // make no further attempt to keep same label order
    this.createGraph();
  }


  @Watch('result')
  public resultChanged(value: string, oldValue: string) {
    this.createGraph();
  }

  public mounted() {
    this.createGraph();
  }

  public createGraph() {

    const MAX_COLS_SHOWN = 20;

    if (this.currentlyShownAttribute) {
      let newIndex = this.result.graphs.findIndex( (rg: any) => this.currentlyShownAttribute === rg.attribute );
      if (newIndex === -1) { // why not 0
        newIndex = Math.min(this.graphIndex, this.result.graphs.length - 1);
      }
      if (this.graphIndex !== newIndex) {
        this.$store.commit('setSelectedGraphIndex', newIndex);
        return; // NB: the watcher on graphIndex will trigger createGraph to run again
      }
    }

    
    const graph = this.result.graphs[this.graphIndex];

    // Sort the bars/result cols by totals unless our
    // optiosn data indicates we shouldn't do this for the attribute.
    if (this.optionsData && this.optionsData.attributes) {
      const attribute = this.optionsData.attributes.find( (obj: any) => {
        return obj.id === graph.attribute;
      });

      if (attribute && !attribute.fixed_order) {
        graph.values.sort((a: any, b: any) => {
          return (b.pass + b.fail) - (a.pass + a.fail);
        });
      }
    }

    // Try to maintain column order from previous data for this graph
    let labels: any[] = [];
    let passArray: any[] = [];
    let failArray: any[] = [];
    if (this.currentlyShownLabels
      && graph.attribute === this.currentlyShownLabels.attribute
    ) { // we are looking at the same attribute as previously selected
      const valuesCopy = graph.values.slice(0);
      this.currentlyShownLabels.labels.forEach( (label: string) => {
        const i = valuesCopy.findIndex( (obj: any) => label === obj.value );
        if (i !== -1) {
          const obj = valuesCopy[i];
          valuesCopy.splice(i, 1);
          labels.push(obj.value);
          passArray.push(obj.pass);
          failArray.push(obj.fail);
        }
      });
      const numberShort = Math.min(MAX_COLS_SHOWN, graph.values.length) - labels.length;
      if (numberShort > 0) {
        labels = labels.concat(valuesCopy.map((obj: any) => obj.value).slice(0, numberShort));
        passArray = passArray.concat(valuesCopy.map((obj: any) => obj.pass).slice(0, numberShort));
        failArray = failArray.concat(valuesCopy.map((obj: any) => obj.fail).slice(0, numberShort));
      }
    } else {
      labels = graph.values.map((obj: any) => obj.value).slice(0, MAX_COLS_SHOWN);
      passArray = graph.values.map((obj: any) => obj.pass).slice(0, MAX_COLS_SHOWN);
      failArray = graph.values.map((obj: any) => obj.fail).slice(0, MAX_COLS_SHOWN);
    }

    // Store column order if graph is interesting (i.e. not just flat 1's)
    if (graph.values.reduce( (acc: any, val: any) => acc + val.pass + val.fail , 0 ) > graph.values.length) {
      // graph is interesing so worth keeping
      this.currentlyShownLabels = {
        attribute: graph.attribute,
        labels
      };
    } else {
      // graph is boring so forget the column order
      this.currentlyShownLabels = null;
    }

    this.currentlyShownAttribute = graph.attribute;

    if (graph.values.length > passArray.length) {
      this.showingTruncatedWarning = true;
    } else {
      this.showingTruncatedWarning = false;
    }

    if (!this.chart) {
      this.chart = new Chart(this.$refs.chartEl as HTMLCanvasElement, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
              backgroundColor: 'rgba(0, 255, 0, .5)',
              borderColor: 'rgba(0, 255, 0, 1)',
              borderSkipped: 'top',
              borderWidth: 1,
              label: 'Pass',
              categoryPercentage: .6,
              data: passArray
          },
          {
              backgroundColor: 'rgba(255, 0, 0, .5)',
              borderColor: 'rgba(255, 0, 0, 1)',
              borderWidth: 1,
              borderSkipped: 'bottom',
              label: 'Fail',
              categoryPercentage: .6,
              data: failArray
          }]
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          scales: {
            xAxes: [{
                stacked: true,
                ticks: {
                    // Include a dollar sign in the ticks
                    callback: (value, index, values) => {
                      return value;
                    }
                }
            }],
            yAxes: [{
                stacked: true,
                ticks: {
                  //@ts-ignore
                  precision: 0
                } 
            }]
          },
          layout: {
            padding: {
              left: 0,
              right: 0,
              top: 0,
              bottom: 0
            }
          },
          tooltips: {
            callbacks: {
              afterBody: (tooltipItem, data) => {
                if (data.datasets) {
                  const passArr = data.datasets[0].data || [];
                  const failArr = data.datasets[1].data || [];
                  const index = tooltipItem[0] ? (tooltipItem[0].index !== undefined ? tooltipItem[0].index : -1)  : -1;
                  if (index > -1) {
                    const rate = this.getPassRate({pass: passArr[index], fail: failArr[index]});
                    return '\n Pass Rate: ' + rate + '%';
                  }
                }
                return '';
              }
            }
          }
        },
      });
    } else {
      this.chart.data.labels = labels;
      this.chart.data.datasets[0].data = passArray;
      this.chart.data.datasets[1].data = failArray;
      this.chart.update();
    }

    this.chart.resize();
  }

  public getPassRate(res: any) {
    if (_.isEmpty(res)) {
      return '-';
    }

    return UtilsService.round((res.pass / (res.pass + res.fail)) * 100);
  }

  public triggerDownload() {
    this.$emit('download');
  }
}
